import { Select } from "antd";
import React from "react";
import getList from "../../universal/getList";

/**
 * CustomSelect 组件的配置项（Props）接口
 */
export interface CustomSelectOptions {
    /** 请求接口时使用的 API 标识，用于区分不同数据源 */
    apiId: string;
    /** 下拉框的原始列表数据（通常为接口返回的数组） */
    getListData: any;
    /** 列表中作为显示文案的字段名，例如 "brandName"、"userName" 等，默认："name" */
    itemName?: string;
    /** 当前发送给接口的分页数据（包含 showPageData 等分页/筛选参数） */
    sendPageData: any;
    /** 传送数据的id */
    sendKeyId: string;
    /** 更新分页数据的 setter，由父组件通过 useState 提供 */
    setSendPageData: (data: any) => void;
    /** 请求数据的函数，接收 apiId 与最新的分页数据作为参数 */
    fetchData: (params: { apiId: string; sendPageData: any }) => void;
    /** 下拉框占位提示文案，默认 "customSelect" */
    placeholder?: string;
    /** 下拉框宽度，默认 130 */
    width?: number;
    /** 默认值，默认：'ALLITEM' */
    defaultValue?: string;
    /** 开启搜索功能 */
    showSearch?: boolean;
}

/**
 * 自定义 Select 下拉选择组件
 *
 * 功能说明：
 * 1. 基于 antd 的 Select 封装，支持搜索、过滤；
 * 2. 选中某一项后，会自动更新分页数据（sendPageData）并触发 fetchData 重新请求；
 * 3. 默认选中 "allBrand"（全部品牌），可通过 getList.general 渲染 options。
 *
 * @param props 见 CustomSelectOptions
 * @returns 渲染好的 Select 组件
 */
const CustomSelect: React.FC<CustomSelectOptions> = ({
    apiId,
    getListData,
    itemName,
    sendPageData,
    setSendPageData,
    fetchData,
    placeholder = "customSelect",
    width = 130,
    sendKeyId,
    defaultValue = 'ALLITEM',
    showSearch = false
}) => {
    /**
     * 选中项发生变化时的回调
     * @param e 当前选中项的值（通常是 id）
     */
    const handleChange = (e: any) => {
        // 基于已有的分页数据，合并最新的筛选条件
        // 合并更新 sendPageData，避免只传 showPageData 把分页字段（pageNum/pageSize 等）冲掉
        const cs = {
            ...sendPageData,
            showPageData: {
                ...sendPageData?.showPageData,
                [sendKeyId]: e,
            },
        };
        // 更新父组件中的分页状态
        setSendPageData(cs);
        // 使用最新的分页数据重新请求列表
        fetchData({ apiId, sendPageData: cs });
    };

    // 受控值：优先取 sendPageData 中对应筛选字段的值，
    // 取不到时回退到 defaultValue（保证首次渲染显示"全部"）
    const value = sendPageData?.showPageData?.[sendKeyId] ?? defaultValue;

    // 判断 options 是否已加载完成，
    // 避免异步加载未完成时 Select 匹配不到 option 而直接显示裸 id（如 "3"）
    const optionsLoaded = Array.isArray(getListData) && getListData.length > 0;

    return (
        <Select
            showSearch={
                showSearch
                    ? {
                        optionFilterProp: "label"
                    }
                    : false
            }
            options={getList.general2(getListData, { itemName })}
            // 占位提示
            placeholder={placeholder}
            // 由非受控 defaultValue 改为受控 value：
            // options 未加载完成时传 undefined（显示 placeholder），加载完成后再传受控值
            value={optionsLoaded ? value : undefined}
            // options 未加载完成时展示 loading，同时禁用交互
            loading={!optionsLoaded}
            disabled={!optionsLoaded}
            // 选中项变化时触发
            onChange={handleChange}
            // 自定义宽度
            style={{ width }}
        >
        </Select>
    );
};

export default CustomSelect;